import { useEffect, useRef, useState } from "react";
import { StreamPeer } from "../lib/peer";
import type { ControlClient } from "../lib/control";

interface Props {
  control: ControlClient;
  iceServers: RTCIceServer[];
  platform: string;
  onPeerState: (state: RTCPeerConnectionState | null) => void;
}

export function LiveView({ control, iceServers, platform, onPeerState }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const peerRef = useRef<StreamPeer | null>(null);
  const [streamMs, setStreamMs] = useState<MediaStream | null>(null);
  const [peerState, setPeerState] = useState<RTCPeerConnectionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [videoAspect, setVideoAspect] = useState<number | null>(null);

  // Tear down on platform change / unmount.
  useEffect(() => {
    setError(null);
    setStreamMs(null);
    setPeerState(null);
    onPeerState(null);

    const peer = new StreamPeer(platform, {
      control,
      iceServers,
      onTrack: (s) => setStreamMs(s),
      onState: (st) => {
        setPeerState(st);
        onPeerState(st);
        if (st === "failed") setError("Peer connection failed — check TURN config.");
      },
    });
    peerRef.current = peer;
    peer.start();

    return () => {
      peer.dispose();
      peerRef.current = null;
    };
  }, [platform, control, iceServers, onPeerState]);

  // Attach the MediaStream to the <video> element when it arrives. Capture
  // the source's intrinsic aspect ratio once metadata loads so the live-frame
  // can adopt it (no letterboxing — works correctly for ultrawide / portrait).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !streamMs) return;
    v.srcObject = streamMs;
    v.play().catch((err) => console.warn("[live] play failed", err));
    const onMeta = () => {
      if (v.videoWidth && v.videoHeight) {
        setVideoAspect(v.videoWidth / v.videoHeight);
      }
    };
    v.addEventListener("loadedmetadata", onMeta);
    onMeta(); // in case it's already loaded
    return () => v.removeEventListener("loadedmetadata", onMeta);
  }, [streamMs]);

  // Reset aspect when the platform changes (new stream = potentially new aspect).
  useEffect(() => {
    setVideoAspect(null);
  }, [platform]);

  // Capture mouse + keyboard input on the overlay, normalize, ship via peer.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    // The overlay covers the whole .live-frame, but the <video> element uses
    // object-fit: contain — so when the captured video's aspect ratio differs
    // from the frame's, the video sits letterboxed inside. Normalize input
    // coords against the actual visible video rect, not the overlay rect.
    function videoContentRect(): { x: number; y: number; width: number; height: number } {
      const v = videoRef.current;
      const o = overlay!;
      const oRect = o.getBoundingClientRect();
      if (!v || !v.videoWidth || !v.videoHeight) {
        return { x: oRect.x, y: oRect.y, width: oRect.width, height: oRect.height };
      }
      const videoAspect = v.videoWidth / v.videoHeight;
      const containerAspect = oRect.width / oRect.height;
      if (videoAspect > containerAspect) {
        // Video is wider — black bars top/bottom.
        const h = oRect.width / videoAspect;
        return { x: oRect.x, y: oRect.y + (oRect.height - h) / 2, width: oRect.width, height: h };
      } else {
        // Video is taller — black bars left/right.
        const w = oRect.height * videoAspect;
        return { x: oRect.x + (oRect.width - w) / 2, y: oRect.y, width: w, height: oRect.height };
      }
    }

    const norm = (e: PointerEvent | WheelEvent | MouseEvent) => {
      const r = videoContentRect();
      const x = r.width === 0 ? 0 : (e.clientX - r.x) / r.width;
      const y = r.height === 0 ? 0 : (e.clientY - r.y) / r.height;
      return {
        x: Math.max(0, Math.min(1, x)),
        y: Math.max(0, Math.min(1, y)),
      };
    };

    const buttonOf = (e: MouseEvent): string => {
      if (e.button === 1) return "middle";
      if (e.button === 2) return "right";
      return "left";
    };

    let lastMoveTime = 0;
    const onMove = (e: PointerEvent) => {
      const now = performance.now();
      // Throttle to ~120 Hz so we don't flood the data channel.
      if (now - lastMoveTime < 8) return;
      lastMoveTime = now;
      const { x, y } = norm(e);
      peerRef.current?.sendInput({ type: "mouseMove", x, y, button: buttonOf(e) });
    };
    const onDown = (e: PointerEvent) => {
      overlay.setPointerCapture?.(e.pointerId);
      const { x, y } = norm(e);
      peerRef.current?.sendInput({
        type: "mouseDown",
        x, y,
        button: buttonOf(e),
        clickCount: e.detail || 1,
      });
    };
    const onUp = (e: PointerEvent) => {
      overlay.releasePointerCapture?.(e.pointerId);
      const { x, y } = norm(e);
      peerRef.current?.sendInput({
        type: "mouseUp",
        x, y,
        button: buttonOf(e),
        clickCount: e.detail || 1,
      });
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { x, y } = norm(e);
      peerRef.current?.sendInput({
        type: "mouseWheel",
        x, y,
        deltaX: -e.deltaX,
        deltaY: -e.deltaY,
      });
    };
    const onContext = (e: MouseEvent) => e.preventDefault();

    const onKey = (e: KeyboardEvent, kind: "keyDown" | "keyUp") => {
      if (document.activeElement && document.activeElement !== document.body && document.activeElement !== overlay) return;
      const modifiers: string[] = [];
      if (e.shiftKey) modifiers.push("shift");
      if (e.ctrlKey) modifiers.push("control");
      if (e.altKey) modifiers.push("alt");
      if (e.metaKey) modifiers.push("meta");
      peerRef.current?.sendInput({ type: kind, keyCode: e.key, modifiers });
      // Only synthesize a 'char' event for regular printable keys on keydown.
      if (kind === "keyDown" && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        peerRef.current?.sendInput({ type: "char", keyCode: e.key, modifiers });
      }
    };
    const onKeyDown = (e: KeyboardEvent) => onKey(e, "keyDown");
    const onKeyUp = (e: KeyboardEvent) => onKey(e, "keyUp");

    overlay.addEventListener("pointermove", onMove);
    overlay.addEventListener("pointerdown", onDown);
    overlay.addEventListener("pointerup", onUp);
    overlay.addEventListener("wheel", onWheel, { passive: false });
    overlay.addEventListener("contextmenu", onContext);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      overlay.removeEventListener("pointermove", onMove);
      overlay.removeEventListener("pointerdown", onDown);
      overlay.removeEventListener("pointerup", onUp);
      overlay.removeEventListener("wheel", onWheel);
      overlay.removeEventListener("contextmenu", onContext);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [platform]);

  return (
    <div className="live-view">
      <div
        className="live-frame"
        style={videoAspect ? { aspectRatio: String(videoAspect) } : undefined}
      >
        <video ref={videoRef} autoPlay playsInline muted />
        <div ref={overlayRef} className="live-overlay" tabIndex={0} />
        {!streamMs && !error && (
          <div className="live-placeholder">connecting to {platform}…</div>
        )}
        {error && <div className="live-placeholder error">{error}</div>}
      </div>
      <div className="live-info">
        <span>platform: <strong>{platform}</strong></span>
        <span>peer: <strong>{peerState ?? "—"}</strong></span>
      </div>
    </div>
  );
}
